import React, { createContext, ReactNode, useContext, useMemo, useRef } from 'react';
import { RecordStatusObservation, useRecordStatusContext } from '@/context/house/documents/RecordStatusContext.tsx';
import {
	deriveDocumentReadinessSummaryFromStatuses,
	deriveKnowledgeBaseReadinessSummaryFromStatuses,
	type DocumentReadinessSummary,
	isKnowledgeBaseActivelyProcessing,
	type KnowledgeBaseReadinessSummary,
	type KnowledgeBaseReadinessState as KnowledgeBaseReadinessStateValue,
} from '@/hooks/house/documents/knowledgeBaseReadiness';

interface UpdateStatusContextType {
	isUpdatingKnowledgeBase: boolean;
	knowledgeBaseReadinessState: KnowledgeBaseReadinessStateValue | null;
	knowledgeBaseReadinessSummary: KnowledgeBaseReadinessSummary | null;
	documentReadinessState: KnowledgeBaseReadinessStateValue | null;
	documentReadinessSummary: DocumentReadinessSummary | null;
}

const UpdateStatusContext = createContext<UpdateStatusContextType | undefined>(undefined);

const sameReadinessSummary = (
	left: KnowledgeBaseReadinessSummary | null,
	right: KnowledgeBaseReadinessSummary | null,
): boolean =>
	left?.state === right?.state &&
	left?.total === right?.total &&
	left?.ready === right?.ready &&
	left?.reading === right?.reading &&
	left?.delayed === right?.delayed &&
	left?.stalled === right?.stalled &&
	left?.failed === right?.failed &&
	left?.attentionDocumentIds.length === right?.attentionDocumentIds.length &&
	(left?.attentionDocumentIds.every((id, index) => id === right?.attentionDocumentIds[index]) ?? true);

const useStableReadinessSummary = <T extends KnowledgeBaseReadinessSummary>(summary: T | null): T | null => {
	const stableSummary = useRef<T | null>(summary);
	if (!sameReadinessSummary(stableSummary.current, summary)) stableSummary.current = summary;
	return stableSummary.current;
};

/**
 * Provider component for sharing update status across the application
 * Tracks when knowledge base updates are in progress
 */
export const UpdateStatusProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
	// Use house-level document status aggregates for knowledge base updating flag
	const { observation, polledStatusById } = useRecordStatusContext();
	const derivedReadinessSummary = useMemo<KnowledgeBaseReadinessSummary | null>(() => {
		if (observation !== RecordStatusObservation.Observed) return null;
		return deriveKnowledgeBaseReadinessSummaryFromStatuses(polledStatusById);
	}, [observation, polledStatusById]);
	const derivedDocumentReadinessSummary = useMemo<DocumentReadinessSummary | null>(() => {
		if (observation !== RecordStatusObservation.Observed) return null;
		return deriveDocumentReadinessSummaryFromStatuses(polledStatusById);
	}, [observation, polledStatusById]);
	// Poll responses can change low-level progress without changing this user-visible projection.
	// Preserve object identity in that case so the global consumers do not all rerender every poll.
	const knowledgeBaseReadinessSummary = useStableReadinessSummary(derivedReadinessSummary);
	const documentReadinessSummary = useStableReadinessSummary(derivedDocumentReadinessSummary);
	const knowledgeBaseReadinessState = knowledgeBaseReadinessSummary?.state ?? null;
	const documentReadinessState = documentReadinessSummary?.state ?? null;
	// TODO(observability): Emit a deduplicated Sentry event when KB readiness first transitions to
	// Failed or Mixed. Include house/document ids and failed pipeline stages for operational review,
	// but exclude filenames, extracted content, and raw errors that may contain sensitive data.
	const isUpdatingKnowledgeBase = isKnowledgeBaseActivelyProcessing(knowledgeBaseReadinessSummary);

	const value = useMemo(
		() => ({
			isUpdatingKnowledgeBase,
			knowledgeBaseReadinessState,
			knowledgeBaseReadinessSummary,
			documentReadinessState,
			documentReadinessSummary,
		}),
		[
			documentReadinessState,
			documentReadinessSummary,
			isUpdatingKnowledgeBase,
			knowledgeBaseReadinessState,
			knowledgeBaseReadinessSummary,
		],
	);

	return <UpdateStatusContext.Provider value={value}>{children}</UpdateStatusContext.Provider>;
};

/**
 * Hook for accessing the update status context
 * Provides information about whether knowledge base updates are in progress
 */
export const useUpdateStatus = (): UpdateStatusContextType => {
	const context = useContext(UpdateStatusContext);
	if (context === undefined) {
		throw new Error('useUpdateStatus must be used within an UpdateStatusProvider');
	}
	return context;
};
